/**
 * <s-lead-phone> — Wraps the lead-capture customer form and copies the typed phone number
 * into the customer tags (e.g. `tel-5511999999999`). The native customer form keeps tags
 * but drops phone numbers; a Shopify Flow workflow moves the tag into the customer's
 * phone field.
 *
 * The tag is written while the buyer types (input/change), not on submit: the storefront
 * captcha intercepts the submit event and resubmits the form programmatically, so submit
 * listeners never run.
 *
 * Required child markup:
 *   [data-lead-phone]           — The phone input.
 *   input[name="contact[tags]"] — The hidden tags input.
 */
class SLeadPhone extends HTMLElement {
  #phoneInput = null;
  #tagsInput = null;
  #baseTags = [];
  #onPhoneChange = () => this.#syncPhoneTag();

  connectedCallback() {
    this.#phoneInput = this.querySelector('[data-lead-phone]');
    this.#tagsInput = this.querySelector('input[name="contact[tags]"]');
    if (!this.#phoneInput || !this.#tagsInput) return;

    this.#baseTags = this.#tagsInput.value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag && !tag.startsWith('tel-'));

    this.#phoneInput.addEventListener('input', this.#onPhoneChange);
    this.#phoneInput.addEventListener('change', this.#onPhoneChange);
    this.#syncPhoneTag();
  }

  disconnectedCallback() {
    this.#phoneInput?.removeEventListener('input', this.#onPhoneChange);
    this.#phoneInput?.removeEventListener('change', this.#onPhoneChange);
  }

  #syncPhoneTag() {
    const tags = [...this.#baseTags];
    const digits = this.#normalize(this.#phoneInput.value);
    if (digits) tags.push(`tel-${digits}`);
    this.#tagsInput.value = tags.join(', ');
  }

  #normalize(value) {
    let digits = value.replace(/\D/g, '');
    if (digits.startsWith('00')) digits = digits.slice(2);
    if (digits.startsWith('0')) digits = digits.slice(1);
    // Brazilian numbers typed without the country code: DDD + 8 or 9 digits.
    if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
    if (digits.length < 12 || digits.length > 15) return '';
    return digits;
  }
}

if (!customElements.get('s-lead-phone')) {
  customElements.define('s-lead-phone', SLeadPhone);
}
